import ks from "@shared/kalmstromShared4";
import { CONFIG } from "./DBConstants";

export default class DBModalManager {

    public static ConfirmCallback: (() => void) | null = null;

    public static OpenModal = (modalId: string): void => {
        if (ks.kErrorMode === true) { return; }
        try {
            const modal: HTMLElement | null = document.getElementById(modalId);
            if (modal === null) { return; }
            modal.classList.add("active");

            const FocusFirstInput = (): void => {
                const firstInput: Element | null = modal.querySelector("input, textarea, select");
                if (firstInput !== null) { (firstInput as HTMLElement).focus(); }
            };
            setTimeout(FocusFirstInput, 100);
        } catch (e) {
            ks.kGlobalErrorHandler(e, "DBModalManager.OpenModal");
        }
    };

    public static CloseModal = (modalId: string): void => {
        if (ks.kErrorMode === true) { return; }
        try {
            const modal: HTMLElement | null = document.getElementById(modalId);
            if (modal === null) { return; }
            modal.classList.remove("active");
        } catch (e) {
            ks.kGlobalErrorHandler(e, "DBModalManager.CloseModal");
        }
    };

    public static CloseAllModals = (): void => {
        if (ks.kErrorMode === true) { return; }
        try {
            const modals: NodeListOf<Element> = document.querySelectorAll(".modal-overlay.active");
            const CloseOne = (modal: Element): void => {
                modal.classList.remove("active");
            };
            modals.forEach(CloseOne);
        } catch (e) {
            ks.kGlobalErrorHandler(e, "DBModalManager.CloseAllModals");
        }
    };

    public static ShowToast = (message: string, type: string): void => {
        if (ks.kErrorMode === true) { return; }
        try {
            const container: HTMLElement | null = document.getElementById("toastContainer");
            if (container === null) { return; }
            const toast: HTMLDivElement = document.createElement("div");
            toast.className = "toast " + type;
            toast.textContent = message;
            container.appendChild(toast);

            const RemoveToast = (): void => {
                toast.remove();
            };
            setTimeout(RemoveToast, CONFIG.TOAST_DURATION);
        } catch (e) {
            ks.kGlobalErrorHandler(e, "DBModalManager.ShowToast");
        }
    };

    public static ShowLoading = (message: string, showProgress: boolean): void => {
        if (ks.kErrorMode === true) { return; }
        try {
            const overlay: HTMLElement | null = document.getElementById("loadingOverlay");
            const messageEl: HTMLElement | null = document.getElementById("loadingMessage");
            const progressEl: HTMLElement | null = document.getElementById("loadingProgress");
            const progressFill: HTMLElement | null = document.getElementById("loadingProgressFill");
            const progressText: HTMLElement | null = document.getElementById("loadingProgressText");

            if (messageEl !== null) { messageEl.textContent = message; }
            if (progressEl !== null) { progressEl.style.display = showProgress ? "block" : "none"; }

            if (showProgress && progressFill !== null && progressText !== null) {
                progressFill.style.width = "0%";
                progressText.textContent = "0%";
            }

            if (overlay !== null) { overlay.classList.add("active"); }
        } catch (e) {
            ks.kGlobalErrorHandler(e, "DBModalManager.ShowLoading");
        }
    };

    public static UpdateLoadingProgress = (percent: number): void => {
        if (ks.kErrorMode === true) { return; }
        try {
            const progressFill: HTMLElement | null = document.getElementById("loadingProgressFill");
            const progressText: HTMLElement | null = document.getElementById("loadingProgressText");

            if (progressFill !== null) { progressFill.style.width = percent + "%"; }
            if (progressText !== null) { progressText.textContent = Math.round(percent) + "%"; }
        } catch (e) {
            ks.kGlobalErrorHandler(e, "DBModalManager.UpdateLoadingProgress");
        }
    };

    public static HideLoading = (): void => {
        if (ks.kErrorMode === true) { return; }
        try {
            const overlay: HTMLElement | null = document.getElementById("loadingOverlay");
            if (overlay !== null) { overlay.classList.remove("active"); }
        } catch (e) {
            ks.kGlobalErrorHandler(e, "DBModalManager.HideLoading");
        }
    };

    public static ShowConfirmation = (options: {
        title?: string;
        message?: string;
        details?: string;
        confirmText?: string;
        confirmLabel?: string;
        onConfirm?: () => void;
    }): void => {
        if (ks.kErrorMode === true) { return; }
        try {
            DBModalManager.SetConfirmContent(options);
            DBModalManager.SetConfirmInput(options);
            DBModalManager.SetConfirmButton(options);

            const modal: HTMLElement | null = document.getElementById("confirmModal");
            if (modal !== null) { modal.classList.add("active"); }
        } catch (e) {
            ks.kGlobalErrorHandler(e, "DBModalManager.ShowConfirmation");
        }
    };

    private static SetConfirmContent = (options: {
        title?: string;
        message?: string;
        details?: string;
    }): void => {
        if (ks.kErrorMode === true) { return; }
        try {
            const titleEl: HTMLElement | null = document.getElementById("confirmModalTitle");
            const messageEl: HTMLElement | null = document.getElementById("confirmMessage");
            const detailsEl: HTMLElement | null = document.getElementById("confirmDetails");

            if (titleEl !== null) { titleEl.textContent = options.title || "Confirm Action"; }
            if (messageEl !== null) { messageEl.textContent = options.message || "Are you sure?"; }

            if (detailsEl !== null) {
                if (options.details) {
                    detailsEl.textContent = options.details;
                    detailsEl.style.display = "block";
                    detailsEl.style.marginTop = "8px";
                    detailsEl.style.padding = "8px";
                    detailsEl.style.background = "var(--bg-secondary)";
                    detailsEl.style.borderRadius = "4px";
                    detailsEl.style.fontSize = "13px";
                } else {
                    detailsEl.style.display = "none";
                }
            }
        } catch (e) {
            ks.kGlobalErrorHandler(e, "DBModalManager.SetConfirmContent");
        }
    };

    private static SetConfirmInput = (options: {
        confirmText?: string;
    }): void => {
        if (ks.kErrorMode === true) { return; }
        try {
            const inputGroup: HTMLElement | null = document.getElementById("confirmInputGroup");
            const input: HTMLInputElement | null = document.getElementById("confirmInput") as HTMLInputElement;
            const confirmBtn: HTMLButtonElement | null = document.getElementById("confirmActionBtn") as HTMLButtonElement;

            if (options.confirmText && inputGroup !== null && input !== null && confirmBtn !== null) {
                inputGroup.style.display = "block";
                input.value = "";
                input.placeholder = 'Type "' + options.confirmText + '" to confirm';
                confirmBtn.disabled = true;

                const CheckInput = (): void => {
                    confirmBtn.disabled = input.value !== options.confirmText;
                };
                input.oninput = CheckInput;

                const FocusInput = (): void => { input.focus(); };
                setTimeout(FocusInput, 100);
            } else if (inputGroup !== null && confirmBtn !== null) {
                inputGroup.style.display = "none";
                confirmBtn.disabled = false;
            }
        } catch (e) {
            ks.kGlobalErrorHandler(e, "DBModalManager.SetConfirmInput");
        }
    };

    private static SetConfirmButton = (options: {
        confirmLabel?: string;
        onConfirm?: () => void;
    }): void => {
        if (ks.kErrorMode === true) { return; }
        try {
            const confirmBtn: HTMLButtonElement | null = document.getElementById("confirmActionBtn") as HTMLButtonElement;
            const modal: HTMLElement | null = document.getElementById("confirmModal");

            if (confirmBtn !== null) {
                confirmBtn.textContent = options.confirmLabel || "Confirm";
                const HandleConfirmClick = (): void => {
                    if (modal !== null) { modal.classList.remove("active"); }
                    if (options.onConfirm) { options.onConfirm(); }
                };
                confirmBtn.onclick = HandleConfirmClick;
            }
        } catch (e) {
            ks.kGlobalErrorHandler(e, "DBModalManager.SetConfirmButton");
        }
    };

    public static HandleContextMenu = (x: number, y: number, listId: string): void => {
        if (ks.kErrorMode === true) { return; }
        try {
            const menu: HTMLElement | null = document.getElementById("contextMenu");
            if (menu === null) { return; }

            menu.style.left = x + "px";
            menu.style.top = y + "px";
            menu.classList.add("active");
            menu.setAttribute("data-list-id", listId);
        } catch (e) {
            ks.kGlobalErrorHandler(e, "DBModalManager.HandleContextMenu");
        }
    };

    public static CloseContextMenu = (): void => {
        if (ks.kErrorMode === true) { return; }
        try {
            const menu: HTMLElement | null = document.getElementById("contextMenu");
            if (menu !== null) { menu.classList.remove("active"); }
        } catch (e) {
            ks.kGlobalErrorHandler(e, "DBModalManager.CloseContextMenu");
        }
    };

    public static ShowKeyboardShortcuts = (): void => {
        if (ks.kErrorMode === true) { return; }
        try {
            const modal: HTMLElement | null = document.getElementById("keyboardShortcutsModal");
            if (modal !== null) { modal.classList.add("active"); }
        } catch (e) {
            ks.kGlobalErrorHandler(e, "DBModalManager.ShowKeyboardShortcuts");
        }
    };
}
